import * as React from 'react'
import {Box} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

/**
 * MIRRORS `freeze-v2/components/KeyValue.js`.
 *
 * COPIED, NOT IMPORTED. disputes-v2 and freeze-v2 are deliberately independent
 * modules: neither may import from the other, so a shared component travels as
 * a copy and any divergence is stated here rather than hidden behind a prop.
 *
 * Divergence from the original: none — byte-identical apart from this header.
 * (`disputes-v2/freeze/` sits at the same depth below `components/` as
 * `freeze-v2/components/`, so even the style import specifiers are unchanged.)
 *
 * Definition-list primitive: muted label, bold right-aligned value with
 * tabular numerals. The freeze drawer renders several label/value fact
 * blocks, so this is built once rather than several times.
 *
 * Real `<dl>/<dt>/<dd>` semantics via MUI's `component` prop, not a grid of
 * anonymous `<div>`s — a screen reader announces a definition list's
 * term/description pairing, and every consumer here is a plain fact sheet a
 * merchant reads, not an interactive widget.
 *
 * `columns` repeats the label/value column pair side by side (e.g.
 * `columns={2}` for a card with more stats than a single tall column reads
 * well). It's still one `<dl>`, so `<dt>`/`<dd>` order in the DOM — and
 * therefore what a screen reader hears — never depends on the visual column
 * count; CSS Grid auto-placement fills left-to-right, top-to-bottom, so an
 * `items` array that isn't a multiple of `columns` simply leaves the last row
 * short.
 *
 * @param {Array<{label: React.ReactNode, value: React.ReactNode, hint?: React.ReactNode}>} items
 * @param {number} columns  how many label/value column pairs to lay out side by side
 */
export default function KeyValue({items, columns = 1}) {
  return (
    <Box
      component='dl'
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, auto 1fr)`,
        columnGap: GAP.lg,
        rowGap: GAP.xs,
        m: 0,
      }}
    >
      {items.map((item, i) => (
        <React.Fragment key={i}>
          <Box component='dt' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, m: 0}}>
            {item.label}
          </Box>
          <Box
            component='dd'
            sx={{
              fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY,
              m: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
            }}
          >
            {item.value}
            {item.hint && (
              <Box
                component='span'
                sx={{
                  display: 'block', fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.regular,
                  color: SUBTLE, fontVariantNumeric: 'normal', mt: 0.25,
                }}
              >
                {item.hint}
              </Box>
            )}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  )
}
